import { Tooltip, Dropdown, Menu } from 'antd';
import { AmbientloadIcon, CopiesIcon, ExpandIcon } from '../../../../icons';
import { Button } from '../../../../components/baseComponents';
import { showToastSuccess } from '../../../../utilities/toast';
import { PrintIcon } from 'src/icons';
import { useCallback, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useAuth } from '@frontegg/react';
import VCASummaryPrintPreview from './VCASummaryPrintPreview';
import { sanitizeHtmlContent } from 'src/utils';

const ScheduleEditorButtons = ({
  textAreaValue,
  getUuid,
  handleReloadIntakeSummary,
  isOverlayVisible,
}) => {
  const componentRef = useRef(null);
  const onBeforeGetContentResolve = useRef(null);

  const { user } = useAuth();
  const isMobile = window.innerWidth < 1260;
  const tooltipCopyTitle = <span className="">Copy to clipboard</span>;
  const tooltipTitle = <span className="text-sm">Regenerate note</span>;

  const handleOnBeforeGetContent = useCallback(() => {
    return new Promise((resolve) => {
      onBeforeGetContentResolve.current = resolve;

      if (
        (getUuid.appointment_details?.llm_intake_summary ||
          getUuid.appointment_details?.llm_intake_summary === '') &&
        typeof onBeforeGetContentResolve.current === 'function'
      ) {
        onBeforeGetContentResolve.current();
      }
    });
  }, [getUuid.appointment_details?.llm_intake_summary]);

  const recordTitle = `${getUuid?.patient_details?.first_name} ${getUuid?.patient_details?.last_name} - ${getUuid?.appointment_details?.condition_name} - ${getUuid?.appointment_details?.start_date}`;

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: recordTitle,
    onBeforeGetContent: handleOnBeforeGetContent,
    onAfterPrint: () => {
      // Reset the Promise resolve so we can print again
      onBeforeGetContentResolve.current = null;
      // setSelectedRecord();
    },
  });

  const handlePrintWithAnalytics = () => {
    // eslint-disable-next-line no-undef
    analytics.track('Clicked Print Note From Appointment Summary', {
      mobile: isMobile,
      description: recordTitle,
      created_at: getUuid.appointment_details?.created_at,
      email: user?.email,
    });
    handlePrint();
  };

  const handleCopyRichText = () => {
    let CopyText = textAreaValue;

    if (getUuid?.appointment_details?.llm_intake_summary_forms) {
      CopyText +=
        getUuid?.appointment_details?.llm_intake_summary_forms.replaceAll(
          '\n',
          '<br>'
        );
    }

    const tempElement = document.createElement('div');
    const patientName = `Patient Name: ${getUuid?.patient_details?.first_name} ${getUuid?.patient_details?.last_name}`;
    const patientDob = `DOB: ${getUuid?.patient_details?.birth_date}`;

    // Sanitize HTML content before inserting
    tempElement.innerHTML = sanitizeHtmlContent(
      patientName + '<br>' + patientDob + '<br><br>' + CopyText
    );

    document.body.appendChild(tempElement);
    const range = document.createRange();
    range.selectNode(tempElement);
    window.getSelection().removeAllRanges();
    window.getSelection().addRange(range);
    document.execCommand('copy');
    document.body.removeChild(tempElement);
    showToastSuccess('Copied to clipboard');
  };

  const handleCopyPlainText = () => {
    let CopyText = textAreaValue;

    if (getUuid?.appointment_details?.llm_intake_summary_forms) {
      CopyText += getUuid?.appointment_details?.llm_intake_summary_forms;
    }
    const patientInfo = `Patient Name: ${getUuid?.patient_details?.first_name} ${getUuid?.patient_details?.last_name}\nDOB: ${getUuid?.patient_details?.birth_date}\n\n`;
    const plainText =
      patientInfo +
      CopyText.replace(/<p>/g, '')
        .replace(/<\/p>/g, '\n')
        .replace(/<strong style="color: rgb\(0, 208, 145\);">/g, '| ')
        .replace(/<strong style=['"]font-weight:\s*600;?['"]>/g, '')
        .replace(/<strong style="color: rgb\(0, 0, 0\);">/g, '')
        .replace(/<strong style="color: #00d091;">/g, '| ')
        .replace(/<strong style='color: #00d091;'>/g, '| ')
        .replace(/<strong style="color: #000;">/g, '')
        .replace(/<strong style='color: #000;'>/g, '')
        .replace(/<strong>/g, '')
        .replace(/<\/strong>/g, '')
        .replace(/<br>/g, '\n')
        .replace(/\n\n+/g, '\n\n')
        .replace(/^\|+/gm, '')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&nbsp;/g, ' ')
        .replace(/&cent;/g, '¢')
        .replace(/&pound;/g, '£')
        .replace(/&yen;/g, '¥')
        .replace(/&euro;/g, '€')
        .replace(/&copy;/g, '©')
        .replace(/&reg;/g, '®')
        .replace(/&trade;/g, '™')
        .trim();

    // Create a temporary textarea element
    const tempTextArea = document.createElement('textarea');
    tempTextArea.style.position = 'fixed';
    tempTextArea.style.opacity = '0';
    tempTextArea.value = plainText;

    document.body.appendChild(tempTextArea);

    // Select and copy the content from the temporary textarea
    tempTextArea.select();
    document.execCommand('copy');

    document.body.removeChild(tempTextArea);

    showToastSuccess('Copied to clipboard');
  };

  const copyOptions = () => {
    return (
      <Menu
        onClick={({ key }) => {
          if (key === 'copy-editor') {
            handleCopyRichText();
          }
        }}
      >
        <Menu.Item key="copy-editor">
          <span className="dropdown-text">Copy as rich text</span>
        </Menu.Item>
      </Menu>
    );
  };
  return (
    <>
      <span
        className={`flex items-center justify-end mb-3.5 flex-wrap z-50 ${
          isMobile ? 'p-2 gap-1' : 'mx-3 gap-3'
        }`}
      >
        <div className="flex" style={{ fontFamily: 'Poppins' }}>
          <Tooltip
            title={isMobile ? null : tooltipCopyTitle}
            placement="topLeft"
          >
            <>
              <Dropdown.Button
                type="primary"
                placement="bottomLeft"
                icon={<ExpandIcon fill={'#ffffff'} />}
                overlay={copyOptions()}
                onClick={handleCopyPlainText}
                rootClassName=" rounded-xl drop-shadow-sm flex bg-[#00D090] hover:bg-[#059669]"
              >
                <div className="flex items-center cursor-pointer gap-1 text-white font-normal">
                  <span>
                    <CopiesIcon />
                  </span>
                  <span>Copy all</span>
                </div>
              </Dropdown.Button>
            </>
          </Tooltip>
        </div>
        <div className="flex items-center">
          <span className="custom-category-text">
            <Tooltip title={isMobile ? null : tooltipTitle} placement="topLeft">
              <>
                <Button
                  className={`rounded-xl drop-shadow-sm flex bg-[#00D090] hover:bg-[#059669] p-2`}
                  onClick={handleReloadIntakeSummary}
                >
                  <div className="flex items-center mx-1.5 cursor-pointer">
                    <AmbientloadIcon className={`cursor-pointer mr-2.5`} />
                    {isOverlayVisible ? 'Regenerating...' : 'Regenerate'}
                  </div>
                </Button>
              </>
            </Tooltip>
          </span>
        </div>

        <Tooltip title="Print note" placement="topRight">
          <span
            className="dropdown-text flex items-center space-x-2"
            onClick={handlePrintWithAnalytics}
          >
            <PrintIcon />
          </span>
        </Tooltip>
      </span>

      <div className="hidden">
        <div ref={componentRef} className="p-4">
          <VCASummaryPrintPreview
            record={getUuid.appointment_details}
            getUuid={getUuid}
          />
        </div>
      </div>
    </>
  );
};

export default ScheduleEditorButtons;
